import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { getSlotRulesFromStore } from "../../../services/utils/date-and-time.utils";

const AMERICA_NEW_YORK = "America/New_York";

export const SLOT_INTERVAL_MINUTES = 15;

export const DEFAULT_SLOT_START_HOUR = 0;
export const DEFAULT_SLOT_END_HOUR = 24;

export type TimeSlotMeta = {
  index: number;
  minutesFromMidnight: number;
  label: string;
};

export type SlotPeriodId = "morning" | "noon" | "evening";

export const SLOT_PERIOD_ORDER: SlotPeriodId[] = ["morning", "noon", "evening"];

export const SLOT_PERIOD_LABELS: Record<SlotPeriodId, string> = {
  morning: "Morning",
  noon: "Noon",
  evening: "Evening",
};

export function getSlotPeriodIdForMinutes(
  minutesFromMidnight: number
): SlotPeriodId {
  const h = Math.floor(minutesFromMidnight / 60);
  if (h < 12) return "morning";
  if (h < 17) return "noon";
  return "evening";
}

export function groupSlotsByPeriod(
  slots: TimeSlotMeta[]
): Record<SlotPeriodId, TimeSlotMeta[]> {
  const groups: Record<SlotPeriodId, TimeSlotMeta[]> = {
    morning: [],
    noon: [],
    evening: [],
  };
  for (const s of slots) {
    groups[getSlotPeriodIdForMinutes(s.minutesFromMidnight)].push(s);
  }
  return groups;
}

export type SlotSelectionPayload = {
  date: Date;
  startTime: Date;
  endTime: Date;
  type?: "datetimerange";
};

export type MultiSlotSelectionPayload = {
  date: Date;
  type: "multislot";
  intervals: Array<{ startTime: Date; endTime: Date }>;
};

export function toggleSlotIndex(indices: number[], index: number): number[] {
  const next = new Set(indices);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  return Array.from(next).sort((a, b) => a - b);
}

export function format12hLabelFromMinutes(minutesFromMidnight: number): string {
  const h = Math.floor(minutesFromMidnight / 60);
  const m = minutesFromMidnight % 60;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? "am" : "pm";
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function format24hLabelFromMinutes(minutesFromMidnight: number): string {
  const h = Math.floor(minutesFromMidnight / 60);
  const m = minutesFromMidnight % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function generateTimeSlots(
  startHour: number = DEFAULT_SLOT_START_HOUR,
  endHour: number = DEFAULT_SLOT_END_HOUR
): TimeSlotMeta[] {
  const slots: TimeSlotMeta[] = [];
  let minutes = startHour * 60;
  const endMinutes = endHour * 60;
  let index = 0;

  while (minutes < endMinutes) {
    slots.push({
      index,
      minutesFromMidnight: minutes,
      label: format12hLabelFromMinutes(minutes),
    });
    minutes += SLOT_INTERVAL_MINUTES;
    index += 1;
  }

  return slots;
}

export function combineDayjsWithMinutes(
  selectedDate: Dayjs,
  minutesFromMidnight: number
): Date {
  const h = Math.floor(minutesFromMidnight / 60);
  const m = minutesFromMidnight % 60;
  return selectedDate.hour(h).minute(m).second(0).millisecond(0).toDate();
}

export function addMinutesToWallClock(
  selectedDate: Dayjs,
  minutesFromMidnight: number,
  add: number
): Date {
  return dayjs(combineDayjsWithMinutes(selectedDate, minutesFromMidnight))
    .add(add, "minute")
    .toDate();
}

export function buildSlotSelectionPayload(
  selectedDate: Dayjs,
  slots: TimeSlotMeta[],
  fromIndex: number,
  toIndex: number | null
): SlotSelectionPayload {
  const start = combineDayjsWithMinutes(
    selectedDate,
    slots[fromIndex].minutesFromMidnight
  );

  let end: Date;
  let type: SlotSelectionPayload["type"];

  if (toIndex === null) {
    end = addMinutesToWallClock(
      selectedDate,
      slots[fromIndex].minutesFromMidnight,
      SLOT_INTERVAL_MINUTES
    );
  } else {
    end = addMinutesToWallClock(
      selectedDate,
      slots[toIndex].minutesFromMidnight,
      SLOT_INTERVAL_MINUTES
    );
    type = "datetimerange";
  }

  return {
    date: selectedDate.startOf("day").toDate(),
    startTime: start,
    endTime: end,
    type,
  };
}

export function isCalendarDateDisabledForSlots(date: Dayjs): boolean {
  const { allowWeekends, minDaysAhead } = getSlotRulesFromStore();
  const ny = moment.tz(date.format("YYYY-MM-DD"), AMERICA_NEW_YORK).startOf("day");
  const dow = ny.day();
  if (!allowWeekends && (dow === 0 || dow === 6)) {
    return true;
  }

  const todayNy = moment.tz(AMERICA_NEW_YORK).startOf("day");
  const daysDiff = ny.diff(todayNy, "days");
  if (daysDiff < 0) {
    return true;
  }
  return daysDiff < minDaysAhead;
}

const MAX_MONTHS_TO_SCAN = 24;

export function getCalendarReferenceDate(): Dayjs {
  const start = dayjs().startOf("month");
  for (let monthOffset = 0; monthOffset < MAX_MONTHS_TO_SCAN; monthOffset++) {
    const monthStart = start.add(monthOffset, "month");
    const daysInMonth = monthStart.daysInMonth();
    for (let d = 1; d <= daysInMonth; d++) {
      const candidate = monthStart.date(d).startOf("day");
      if (!isCalendarDateDisabledForSlots(candidate)) {
        return monthStart.startOf("month");
      }
    }
  }
  return start;
}

export const SLOT_MODAL_EMPTY_PLACEHOLDER_PT = 1.5;
export const SLOT_MODAL_EMPTY_PLACEHOLDER_PX = 1;

export type MultiDateMultiSlotPayload = {
  type: "multidate-multislot";
  days: MultiSlotSelectionPayload[];
};

export function dayjsFromDateKey(key: string): Dayjs {
  return dayjs(key).startOf("day");
}

export function toSelectedDateKey(d: Dayjs): string {
  return d.startOf("day").format("YYYY-MM-DD");
}

export function sortSelectedDateKeys(keys: string[]): string[] {
  return [...keys].sort();
}
